import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import { pushBrowserNav } from '@/hooks/useBrowserHistory';

import type { LibrarySection } from '@/types';

interface UseSeeAllNavigationParams {
  sections: LibrarySection[];
}

interface SeeAllNavigation {
  seeAllSection: LibrarySection | null;
  openSeeAll: (slug: string) => void;
}

const selector = (store: IAppStore) => ({
  seeAllCategorySlug: store.seeAllCategorySlug,
  setSeeAllCategorySlug: store.setSeeAllCategorySlug,
  setSeeAllCategoryTitle: store.setSeeAllCategoryTitle,
});

/**
 * Owns the card-row home's in-app "See all" navigation: the selected category
 * and the browser-history back-step. A `genaicategory` deeplink no longer
 * enters here — `CategoryRowsHome` pins that section as its first row instead
 * (`getPinnedDeeplinkCategory`). Desktop is unaffected either way; it stays on
 * the `categoryHandler` scroll-to-row path.
 */
const useSeeAllNavigation = ({
  sections,
}: UseSeeAllNavigationParams): SeeAllNavigation => {
  const { seeAllCategorySlug, setSeeAllCategorySlug, setSeeAllCategoryTitle } =
    useAppStore(selector);

  const openSeeAll = useCallback(
    (slug: string) => setSeeAllCategorySlug(slug),
    [setSeeAllCategorySlug]
  );

  // Push one browser-history entry per null -> non-null transition, so the back
  // button returns to the rows.
  const prevSlugRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSlugRef.current;
    prevSlugRef.current = seeAllCategorySlug;
    if (prev === null && seeAllCategorySlug) {
      pushBrowserNav('seeAll');
    }
  }, [seeAllCategorySlug]);

  const seeAllSection = useMemo(
    () =>
      sections.find((section) => section.slug === seeAllCategorySlug) ?? null,
    [sections, seeAllCategorySlug]
  );

  useEffect(() => {
    setSeeAllCategoryTitle(seeAllSection?.title ?? null);
  }, [seeAllSection, setSeeAllCategoryTitle]);

  return { seeAllSection, openSeeAll };
};

export default useSeeAllNavigation;
